"use client";

import { useEffect, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import {
  FaCheckCircle,
  FaFileContract,
  FaFileInvoice,
  FaShoppingCart,
  FaUserPlus,
  FaStar,
} from "react-icons/fa";

interface StepItem {
  id: string;
  title: string;
  icon: React.ReactNode;
  steps: string[];
}

const stepsData: StepItem[] = [
  {
    id: "register",
    title: "How to Register",
    icon: <FaUserPlus className="text-2xl text-green-800" />,
    steps: [
      `Click the "Register" button on the header navigation.`,
      "Enter your active mobile number and verify it with the OTP sent.",
      "Complete the buyer registration form (Name, Email, Password, Address, Pin Code, and Profile Picture).",
      "Log in securely to start browsing all available agribusiness listings.",
    ],
  },
  {
    id: "buy",
    title: "How to Buy or Inquire",
    icon: <FaShoppingCart className="text-2xl text-green-800" />,
    steps: [
      "Browse catalog categories: Agri Inputs (fertilizers, seeds, tools, machinery), Agri Outputs (fruits, vegetables), or Agri Services (on-farm/off-farm support).",
      "Use search queries to find matching products or post custom requirements.",
      "Review listing details, pricing sheets, and direct contact options.",
      "Submit an enquiry form or click 'Call Vendor' to establish direct connection.",
    ],
  },
];

const benefits: string[] = [
  "Free access to a wide range of agricultural products, inputs, and services.",
  "Compare prices, technical specifications, and photos before purchasing.",
  "Establish direct contact with local, verified vendors.",
  "Convenient and reliable way to fulfill your farming and commercial needs.",
];

const terms: string[] = [
  "Free Access: Buyers browse listings, post requirements, and contact vendors with zero commission or transaction fees.",
  "Mandatory Registration: Buyers must complete a one-time registration with accurate contact details before submitting enquiries.",
  "Authentic Use Only: The platform must only be used for genuine agribusiness enquiries. Misuse or spam postings are strictly prohibited.",
  "Platform Rights: Karnataka Agribusiness reserves the right to restrict access if buyer activities violate platform guidelines.",
];

const buyersFeedbackContent = {
  heading: "Buyers Feedback",
  intro:
    "We value buyer feedback. It helps us optimize the experience for everyone. Please rate and share your feedback on:",
  points: [
    "Platform usability and listing search flow.",
    "Quality and description accuracy of listed products/services.",
    "Communication speed with vendors and support.",
    "Specific suggestions for feature improvements.",
  ],
  closing: "Your reviews help other farmers and buyers make informed, reliable choices.",
};

function BuyersToolKitContent() {
  const searchParams = useSearchParams();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          const yOffset = -99;
          const y =
            el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 100);
    }
  }, [searchParams]);

  return (
    <div className="bg-white min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-12">
        
        {/* Header Block */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-sm bg-green-50 text-green-800 mb-2">
            <FaShoppingCart className="text-3xl" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-green-900 tracking-tight">
            Buyer Tool Kit
          </h1>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto font-medium">
            Your step-by-step guide to registering, exploring listings, contacting vendors, and submitting feedback on the Karnataka Agribusiness platform.
          </p>
        </div>

        {/* Section 1: Registration & How to Buy */}
        <div id="REG" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm space-y-8">
          {stepsData.map(({ id, title, icon, steps }) => (
            <div key={id} className="space-y-4">
              <h2 className="flex items-center gap-3 text-green-800 text-xl font-bold border-b border-gray-155 pb-3">
                {icon}
                {title}
              </h2>
              <ol className="space-y-3">
                {steps.map((step, i) => (
                  <li key={i} className="flex gap-4">
                    <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-sm bg-green-50 text-green-800 font-bold text-xs">
                      {i + 1}
                    </span>
                    <span className="text-gray-800 text-sm sm:text-base font-medium leading-relaxed">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>

        {/* Section 2: Buyer Benefits */}
        <div id="enquiry" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm">
          <h2 className="flex items-center gap-3 text-green-800 text-xl font-bold border-b border-gray-155 pb-3 mb-5">
            <FaCheckCircle className="text-2xl text-green-800" />
            <span>Buyer Benefits</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {benefits.map((benefit, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-sm border border-gray-200">
                <span className="mt-1.5 w-2 h-2 rounded-sm bg-green-800 shrink-0" />
                <span className="text-gray-800 text-sm font-medium leading-relaxed">
                  {benefit}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Section 3: Terms & Conditions */}
        <div id="GFB" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm">
          <h2 className="flex items-center gap-3 text-green-800 text-xl font-bold border-b border-gray-155 pb-3 mb-5">
            <FaFileContract className="text-2xl text-green-800" />
            <span>Buyers Terms &amp; Conditions</span>
          </h2>
          <ul className="space-y-4">
            {terms.map((term, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 rounded-sm bg-green-800 shrink-0" />
                <span className="text-gray-800 text-sm sm:text-base font-medium leading-relaxed">
                  {term}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Section 4: Feedback */}
        <div id="BF" className="bg-white border border-gray-200 rounded-sm p-6 sm:p-8 shadow-sm">
          <h2 className="flex items-center gap-3 text-green-800 text-xl font-bold border-b border-gray-155 pb-3 mb-5">
            <FaFileInvoice className="text-2xl text-green-800" />
            <span>{buyersFeedbackContent.heading}</span>
          </h2>
          <div className="space-y-4">
            <p className="text-gray-800 text-sm sm:text-base font-semibold leading-relaxed">
              {buyersFeedbackContent.intro}
            </p>
            <ul className="space-y-3">
              {buyersFeedbackContent.points.map((point, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-2 h-2 rounded-sm bg-green-800 shrink-0" />
                  <span className="text-gray-800 text-sm font-medium leading-relaxed">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-xs sm:text-sm italic text-gray-500 pt-3 border-t border-gray-155 flex items-center gap-1.5 font-medium">
              <FaStar className="text-yellow-500" />
              <span>{buyersFeedbackContent.closing}</span>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

export default function BuyersToolKitPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-gray-500 font-medium text-sm">Loading Buyer Tool Kit...</div>}>
      <BuyersToolKitContent />
    </Suspense>
  );
}
